import React from 'react';
import CamUpgradeModal from 'screen/auth/login/CamUpgradeModal';
import CamSessionExpiredModal from 'screen/auth/login/CamSessionExpiredModal';
import {
  CAM_SESSION_EXPIRED_EVENT,
  CAM_UPGRADE_REQUIRED_EVENT,
  buildCamLoginUrl,
  dismissCamUpgradeForLater,
  dismissCamUpgradeForTrial,
  getCamBaseUrl,
  isCamUpgradeModalPending,
  requestCamNavigate,
} from 'utils/camAuth';
import { getAccessToken, getUserId, removeAccessToken, removeUserId } from 'utils/sessionStorage';
import toast from 'react-hot-toast';

/**
 * Global CAM upgrade gate — mounted outside Router (index.tsx) so URL/hash
 * changes during login never unmount it. Also hosts the shared "CAM session
 * expired" dialog: every "Open CAM" button (sidebar, dashboard tile, college
 * list, etc.) calls notifyCamSessionExpired() instead of owning its own
 * modal/state, so the prompt and its "log in to CAM" action live in one place.
 */
const CamUpgradeModalHost = () => {
  const [userDismissed, setUserDismissed] = React.useState(false);
  const [, bump] = React.useReducer((n: number) => n + 1, 0);
  const [isSessionExpiredOpen, setIsSessionExpiredOpen] = React.useState(false);

  React.useLayoutEffect(() => {
    const onRequired = () => {
      setUserDismissed(false);
      bump();
    };
    if (isCamUpgradeModalPending()) {
      onRequired();
    }
    window.addEventListener(CAM_UPGRADE_REQUIRED_EVENT, onRequired);
    return () => window.removeEventListener(CAM_UPGRADE_REQUIRED_EVENT, onRequired);
  }, []);

  React.useEffect(() => {
    const onSessionExpired = () => setIsSessionExpiredOpen(true);
    window.addEventListener(CAM_SESSION_EXPIRED_EVENT, onSessionExpired);
    return () => window.removeEventListener(CAM_SESSION_EXPIRED_EVENT, onSessionExpired);
  }, []);

  const token = getAccessToken();
  const userId = getUserId();
  const open = !userDismissed && isCamUpgradeModalPending() && !!token && !!userId;

  return (
    <>
      <CamUpgradeModal
        open={open}
        handleClose={() => {
          setUserDismissed(true);
          dismissCamUpgradeForLater();
          requestCamNavigate('login');
        }}
        handleUpgrade={() => {
          setUserDismissed(true);
          dismissCamUpgradeForTrial();
          requestCamNavigate('upgrade-plan');
        }}
      />

      <CamSessionExpiredModal
        open={isSessionExpiredOpen}
        onClose={() => setIsSessionExpiredOpen(false)}
        onLogin={() => {
          setIsSessionExpiredOpen(false);
          if (!getCamBaseUrl()) {
            toast.error('CAM is not configured. Please contact support.');
            return;
          }
          // Send the user straight to CAM's login page. CAM will detect there
          // is no valid GradMap session and redirect through GradMap OAuth
          // login, landing the user back in CAM once they re-authenticate.
          window.location.href = buildCamLoginUrl('/');
        }}
      />
    </>
  );
};

export default CamUpgradeModalHost;
